import { useEffect, useState } from "react";
import api from "../api/axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import AlertBox from "../components/AlertBox";
import { useTranslation } from "react-i18next";

const API_BASE = "https://blog.wageh.net";

export default function OrderDetails() {
    const { id } = useParams();
    const nav = useNavigate();
    const { t } = useTranslation();

    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [msg, setMsg] = useState("");

    const fetchOrder = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/orders/${id}`);
            const payload = res.data.data ?? res.data;
            setOrder(payload.data ?? payload);
        } catch (e) {
            setErr(e.response?.data?.message || t("order_details_load_error"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    const getStatusInfo = (status) => {
        switch (status?.toLowerCase()) {
            case "paid":
                return { color: "success", icon: "bi-check-circle-fill", label: t("status_paid") };
            case "pending":
                return { color: "warning", icon: "bi-clock-history", label: t("status_pending") };
            case "shipped":
                return { color: "primary", icon: "bi-truck", label: t("status_shipped") };
            case "cancelled":
                return { color: "danger", icon: "bi-x-circle-fill", label: t("status_cancelled") };
            default:
                return { color: "secondary", icon: "bi-info-circle-fill", label: status || t("status_unknown") };
        }
    };

    if (loading) {
        return (
            <div className="container py-5 text-center my-5">
                <div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }} role="status" />
                <p className="mt-3 text-muted fw-bold">{t("loading_invoice")}</p>
            </div>
        );
    }

    if (err || !order) {
        return (
            <div className="container py-5">
                <AlertBox type="danger" message={err || t("order_not_found")} onClose={() => setErr("")} />
                <button onClick={() => nav("/orders")} className="btn btn-outline-secondary mt-3">
                    <i className="bi bi-arrow-right me-2"></i> {t("back_to_orders")}
                </button>
            </div>
        );
    }

    const statusInfo = getStatusInfo(order.status);

    return (
        <div className="container py-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-4 d-print-none">
                <button onClick={() => nav("/orders")} className="btn btn-outline-secondary">
                    <i className="bi bi-arrow-right me-2"></i> {t("back_to_orders")}
                </button>
                <button onClick={() => window.print()} className="btn btn-outline-dark">
                    <i className="bi bi-printer me-2"></i> {t("print_invoice")}
                </button>
            </div>

            <AlertBox type="success" message={msg} onClose={() => setMsg("")} />

            <div className="row g-4">
                <div className="col-lg-8">
                    <div className="card shadow-sm border-0">
                        <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                            <h4 className="m-0 fw-bold text-primary">
                                <i className="bi bi-receipt-cutoff me-2"></i> {t("order_number")}{order.id}
                            </h4>
                            <span className="text-muted small">
                                {t("order_date")}{order.created_at?.slice(0, 10) || t("not_available")}
                            </span>
                        </div>

                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-borderless align-middle mb-0">
                                    <thead className="bg-light text-muted small text-uppercase">
                                        <tr>
                                            <th className="ps-4 py-3">{t("product_col")}</th>
                                            <th className="text-center">{t("price_col")}</th>
                                            <th className="text-center">{t("qty_col")}</th>
                                            <th className="text-end pe-4">{t("total_col")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {order.items?.map((item) => {
                                            const cleanPath = item.product?.image ? item.product.image.replace(/^\/+/, '').replace(/^storage\//, '').replace(/^uploads\//, '') : "";
                                            const imgSrc = item.product?.image
                                                ? (item.product.image.startsWith("http") ? item.product.image : `${API_BASE}/uploads/${cleanPath}`)
                                                : null;

                                            return (
                                                <tr key={item.id} className="border-bottom">
                                                    <td className="ps-4 py-3">
                                                        <div className="d-flex align-items-center">
                                                            {imgSrc ? (
                                                                <img src={imgSrc} alt={item.product?.name} className="rounded border me-3 d-none d-sm-block" width="50" height="50" style={{ objectFit: 'contain' }} />
                                                            ) : (
                                                                <div className="bg-light rounded border me-3 d-none d-sm-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                                                    <i className="bi bi-image text-muted"></i>
                                                                </div>
                                                            )}
                                                            <div>
                                                                <Link to={`/products/${item.product?.id}`} className="text-decoration-none fw-bold text-dark">
                                                                    {item.product?.name || t("unknown_product")}
                                                                </Link>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center text-muted">${item.price_snapshot}</td>
                                                    <td className="text-center fw-bold">{item.qty}</td>
                                                    <td className="text-end pe-4 fw-bold text-success">${item.line_total}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-lg-4">
                    <div className="card shadow-sm border-0 position-sticky" style={{ top: "20px" }}>
                        <div className="card-body">
                            <h5 className="fw-bold mb-3 border-bottom pb-2">{t("order_status_title")}</h5>
                            <div className={`alert alert-${statusInfo.color} d-flex align-items-center mb-4`} role="alert">
                                <i className={`bi ${statusInfo.icon} fs-4 me-3`}></i>
                                <div>
                                    <h6 className="alert-heading mb-0 fw-bold">{statusInfo.label}</h6>
                                </div>
                            </div>

                            <h5 className="fw-bold mb-3 border-bottom pb-2">{t("invoice_summary")}</h5>
                            <dl className="row text-muted small mb-2">
                                <dt className="col-6 fw-normal">{t("subtotal")}</dt>
                                <dd className="col-6 text-end">${order.total}</dd>

                                <dt className="col-6 fw-normal">{t("shipping_fees")}</dt>
                                <dd className="col-6 text-end text-success">{t("free")}</dd>
                            </dl>
                            <hr className="my-3" />
                            <dl className="row align-items-center mb-0">
                                <dt className="col-6 h5 mb-0 fw-bold">{t("total")}</dt>
                                <dd className="col-6 text-end h4 text-success fw-bold mb-0">
                                    ${order.total}
                                </dd>
                            </dl>

                            {order.status === "pending" && (
                                <div className="mt-4 pt-3 border-top d-print-none">
                                    <button
                                        className="btn btn-success w-100 py-2 fw-bold shadow-sm"
                                        onClick={() => {
                                            alert(t("redirect_payment_gateway"));
                                        }}
                                    >
                                        <i className="bi bi-credit-card me-2"></i> {t("pay_now")}
                                    </button>
                                    <small className="text-muted d-block mt-2 text-center">
                                        {t("please_complete_payment")}
                                    </small>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @media print {
                    body { background-color: white !important; }
                    .card { box-shadow: none !important; border: 1px solid #ddd !important; }
                    .d-print-none { display: none !important; }
                }
            `}</style>
        </div>
    );
}